/* eslint-disable max-lines */
import * as matomoReactRouter from '@bodymod/matomo/src/util/index';
import PropTypes from 'prop-types';
import { PureComponent } from 'react';
import { connect } from 'react-redux';

import { CategoryTreeType } from 'Type/Category.type';
import { ProductType } from 'Type/ProductList.type';
import { LocationType } from 'Type/Router.type';
import { addElementToHead } from 'Util/Dom';
import history from 'Util/History';
import { getFetch } from 'Util/Request';

import { initMatomo } from '../../store/Matomo/Matomo.action';
import Matomo from './Matomo.component';
import {
    CONFIG_PATH,
    MATOMO,
    MATOMO_VARIABLES,
    ORIGINAL_VARIATION
} from './Matomo.config';

/** @namespace Bodymod/Matomo/Component/Matomo/Container/mapStateToProps */
export const mapStateToProps = (state) => ({
    isMatomoInitialized: state.MatomoReducer.isMatomoInitialized,
    matomo: state.MatomoReducer.matomo,
    category: state.CategoryReducer?.category,
    product: state.ProductReducer?.product
});

/** @namespace Bodymod/Matomo/Component/Matomo/Container/mapDispatchToProps */
export const mapDispatchToProps = (dispatch) => ({
    initMatomo: (matomo) => dispatch(initMatomo(matomo))
});

/** @namespace Bodymod/Matomo/Component/Matomo/Container */
export class MatomoContainer extends PureComponent {
    static propTypes = {
        children: PropTypes.node.isRequired,
        initMatomo: PropTypes.func.isRequired,
        isMatomoInitialized: PropTypes.bool.isRequired,
        location: LocationType.isRequired,
        product: ProductType.isRequired,
        category: CategoryTreeType.isRequired,
        // eslint-disable-next-line react/forbid-prop-types
        matomo: PropTypes.objectOf(PropTypes.any).isRequired
    };

    state = {
        config: {},
        enabled: false,
        initTracingTriggered: false,
        activeTests: [],
        isABTestingEnabled: false,
        location: history.location
    };

    componentDidMount() {
        this.setMatomoConfig();
        history.listen((history) => {
            this.onRouteChanged(history);
        });
    }

    componentDidUpdate() {
        const { enabled, initTracingTriggered, isABTestingEnabled } = this.state;
        const { isMatomoInitialized } = this.props;
        if (enabled && !isMatomoInitialized) {
            this.enableMatomo();
        }
        if (isMatomoInitialized && !initTracingTriggered) {
            if (isABTestingEnabled) {
                this.triggerABtesting();
            }
            this.setState({ initTracingTriggered: true });
        }
    }

    containerProps = () => {
        const {
            children
        } = this.props;

        return {
            children
        };
    };

    setMatomoConfig() {
        if (!window.dataCache) {
            window.dataCache = {};
        }

        if (window.dataCache[MATOMO]) {
            const { config: { matomo_enabled }, config } = window.dataCache[MATOMO];
            this.setState({ config, enabled: matomo_enabled });
        }
        getFetch(`${CONFIG_PATH}?${Date.now()}`, MATOMO).then(
            /** @namespace Bodymod/Matomo/Component/Matomo/Container/MatomoContainer/setMatomoConfig/then/catch/then/then/getFetch/then */
            (response) => {
                // eslint-disable-next-line no-magic-numbers
                if (response.status === 200) {
                    return response.json();
                }

                return {};
            }
        ).then(
            /** @namespace Bodymod/Matomo/Component/Matomo/Container/MatomoContainer/setMatomoConfig/then/catch/then/then */
            (config) => {
                window.dataCache[MATOMO] = { config };
                const { matomo_ab_tests_enabled: isABTestingEnabled, matomo_enabled } = config;
                this.setState({ config, enabled: matomo_enabled, isABTestingEnabled });
            }
        ).catch(
            /** @namespace Bodymod/Matomo/Component/Matomo/Container/MatomoContainer/setMatomoConfig/then/catch */
            (error) => {
                this.setState({ config: {}, enabled: false, isABTestingEnabled: false });
                console.error(error, 'MATOMO BLOCKED');
            }
        );
    }

    enableMatomo() {
        const {
            config: {
                matomo_js_container: clientTrackerName,
                matomo_server_tracker: serverTrackerName,
                matomo_subdomain: baseUrl,
                matomo_domain: url
            }
        } = this.state;
        const { initMatomo } = this.props;

        const matomo = matomoReactRouter({
            baseUrl,
            url: `${ url }/${ baseUrl }`,
            siteId: 1,
            serverTrackerName,
            clientTrackerName
        });

        initMatomo(matomo);
    }

    onRouteChanged(history) {
        const { location: { pathname: prevPathname } = {} } = this.state;
        const { pathname } = history;
        if (prevPathname !== pathname) {
            this.setMatomoConfig();
            this.handleABtesting();
            this.setState({ location: history });
        }
    }

    handleABtesting() {
        const { isABTestingEnabled, activeTests } = this.state;
        this.clearOutdatedTests();
        if (isABTestingEnabled) {
            this.triggerABtesting();
        } else {
            activeTests.forEach((variations) => {
                this.clearABtesting(variations);
            });
        }
    }

    clearOutdatedTests() {
        const { activeTests, config: { matomo_style_ab_tests: abTests } } = this.state;
        activeTests.forEach((variations) => {
            const isActive = abTests.filter((test) => test.VARIATIONS === variations);
            if (!isActive.length) {
                this.clearABtesting(variations);
            }
        });
    }

    triggerABtesting() {
        const {
            config: { matomo_style_ab_tests: abTests }
        } = this.state;
        const newActiveTests = [];
        abTests.forEach((test) => {
            const abTestsOptions = {};
            MATOMO_VARIABLES.forEach((variable) => {
                abTestsOptions[variable] = test[variable];
            });
            this.pushABtesting(abTestsOptions);
            newActiveTests.push(test.VARIATIONS);
        });
        this.setState({ activeTests: newActiveTests });
    }

    clearABtesting(variations) {
        variations.forEach((variation) => {
            const { VARIATION_NAME: name } = variation;
            document.getElementById(name)?.remove();
        });
    }

    pushABtesting(props) {
        const { matomo } = this.props;
        const {
            PERCENTAGE, INCLUDED_TARGETS, EXCLUDED_TARGETS, TEST_NAME, START_DATE_TIME,
            VARIATIONS, END_DATE_TIME
        } = props;
        const _mtm = matomo;

        const variations = [{
            name: ORIGINAL_VARIATION,
            activate() {
            }
        }];

        VARIATIONS.forEach((variation) => {
            const { VARIATION_NAME: name, VARIATION_STYLE: style } = variation;
            variations.push({
                name,
                activate() {
                    if (!document.getElementById(this.name)) {
                        addElementToHead('style', this.name, style);
                    }
                }
            });
        });

        _mtm.push(['AbTesting::create', {
            name: TEST_NAME,
            percentage: PERCENTAGE,
            includedTargets: INCLUDED_TARGETS,
            excludedTargets: EXCLUDED_TARGETS,
            startDateTime: START_DATE_TIME || '',
            endDateTime: END_DATE_TIME || '',
            variations,
            trigger() {
                return true;
            }
        }]);
    }

    render() {
        return (
                <Matomo { ...this.containerProps() } />
        );
    }
}

export default connect(mapStateToProps, mapDispatchToProps)(MatomoContainer);
